import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, DoorOpen, Send, Pencil, Trash2, History, RotateCcw } from "lucide-react";
import ClientShell from "./ClientShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import AudienceGate from "./files/AudienceGate";
import {
  useFeedbackDetail, useEvidenceRoomLaunch, useResponseRoomLaunch, useRetryEvidence,
  useUpdateFeedbackDraft, usePublishFeedback, useDeleteFeedbackDraft, useAmendFeedback,
} from "@/lib/feedbackQueries";
import { useQueryClient } from "@tanstack/react-query";
import { useFileUploadContext } from "@/lib/fileQueries";

const EMPTY_PRIORITY = { observation: "", impact: "", suggestion: "" };

/**
 * Master v5 Prompt 12 — Feedback detail. A draft is fully editable by
 * the coach (never by anyone else); a published row is entirely
 * read-only content — only the separately-linked response WorkDocument
 * is ever editable past this point, via its own Room launch.
 */
export default function FeedbackDetail() {
  const { workspaceId, fbkId } = useParams();
  const navigate = useNavigate();
  const { data, isPending, refetch } = useFeedbackDetail(workspaceId, fbkId);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const evidenceRoom = useEvidenceRoomLaunch(workspaceId);
  const retryEvidence = useRetryEvidence(workspaceId);
  const responseRoom = useResponseRoomLaunch(workspaceId);
  const updateDraft = useUpdateFeedbackDraft(workspaceId, fbkId);
  const publish = usePublishFeedback(workspaceId);
  const deleteDraft = useDeleteFeedbackDraft(workspaceId);
  const amend = useAmendFeedback(workspaceId);
  const queryClient = useQueryClient();

  const [focus, setFocus] = useState("");
  const [priorities, setPriorities] = useState([{ ...EMPTY_PRIORITY }]);
  const [audience, setAudience] = useState(null);

  useEffect(() => {
    if (data?.feedback) {
      setFocus(data.feedback.focus);
      setPriorities(data.feedback.priorities.map((p) => ({ observation: p.observation, impact: p.impact || "", suggestion: p.suggestion || "" })));
    }
  }, [data?.feedback?.fbk_id]);

  useEffect(() => {
    // GET detail marks read server-side — refresh the sidebar badge count
    // right away instead of waiting on its own 30s poll.
    if (data?.feedback?.status === "published") {
      queryClient.invalidateQueries({ queryKey: ["feedback-unread-count", workspaceId] });
    }
  }, [data?.feedback?.fbk_id, data?.feedback?.status, workspaceId, queryClient]);

  if (isPending) {
    return <ClientShell><div className="flex items-center gap-2 text-ink-soft"><Loader2 size={16} className="animate-spin" /> Loading…</div></ClientShell>;
  }
  if (!data) return null;
  const { feedback, evidence, can_manage, is_mine } = data;
  const isDraft = feedback.status === "draft";
  const isTeamWorkspace = uploadContext?.access_type === "team";

  function updatePriority(idx, field, value) {
    setPriorities((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  }

  async function saveDraft() {
    try {
      await updateDraft.mutateAsync({
        focus, priorities: priorities.filter((p) => p.observation.trim()).map((p) => ({
          observation: p.observation.trim(), impact: p.impact.trim() || null, suggestion: p.suggestion.trim() || null,
        })),
      });
      toast.success("Draft saved");
      refetch();
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't save draft");
    }
  }

  async function doPublish() {
    try {
      await publish.mutateAsync({ fbkId, audience_selection: audience });
      toast.success("Feedback published");
      refetch();
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't publish");
    }
  }

  async function doDelete() {
    try {
      await deleteDraft.mutateAsync(fbkId);
      toast.success("Draft discarded");
      navigate(`/rehearsal-room/w/${workspaceId}/feedback`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't discard draft");
    }
  }

  async function doAmend() {
    try {
      const res = await amend.mutateAsync(fbkId);
      navigate(`/rehearsal-room/w/${workspaceId}/feedback/${res.fbk_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't amend this feedback");
    }
  }

  async function openEvidenceRoom(index) {
    try {
      const res = await evidenceRoom.mutateAsync({ fbkId, index });
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "This item has no Rehearsal Room to open");
    }
  }

  async function retryExercise(index) {
    try {
      const res = await retryEvidence.mutateAsync({ fbkId, index });
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't start a retry");
    }
  }

  async function openResponseRoom() {
    try {
      const res = await responseRoom.mutateAsync(fbkId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't open your response");
    }
  }

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="feedback-detail-page">
        <button onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/feedback`)} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-4" data-testid="feedback-detail-back-btn">
          <ArrowLeft size={14} /> Back to Feedback
        </button>

        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isDraft ? "bg-ink/10 text-ink-soft" : "bg-sage/20 text-sage-deep"}`} data-testid="feedback-detail-status">
            {isDraft ? "Draft" : "Published"}
          </span>
          {feedback.version_number > 1 && (
            <span className="text-xs text-ink-soft flex items-center gap-1"><History size={11} /> Version {feedback.version_number}</span>
          )}
        </div>

        {isDraft && can_manage ? (
          <Input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="One focus for this feedback"
            className="font-serif text-xl mb-4" data-testid="feedback-detail-focus-input" />
        ) : (
          <h1 className="font-serif text-2xl text-ink mb-4" data-testid="feedback-detail-focus">{feedback.focus}</h1>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {evidence.map((ev, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs bg-ink/5 px-3 py-1.5 rounded-full" data-testid={`feedback-detail-evidence-${idx}`}>
              <span>{ev.label || ev.target_type}</span>
              {ev.room_eligible && (
                <button onClick={() => openEvidenceRoom(idx)} className="text-terracotta font-medium flex items-center gap-1" data-testid={`feedback-detail-evidence-room-btn-${idx}`}>
                  <DoorOpen size={11} /> Go to the Rehearsal Room
                </button>
              )}
              {is_mine && ["rehearsal", "interaction_attempt"].includes(ev.target_type) && (
                <button onClick={() => retryExercise(idx)} className="text-sage-deep font-medium flex items-center gap-1" data-testid={`feedback-detail-evidence-retry-btn-${idx}`}>
                  <RotateCcw size={11} /> Retry
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-4 mb-8">
          {priorities.map((p, idx) => (
            <div key={idx} className="rounded-xl border border-ink/10 bg-white/70 p-4 space-y-2" data-testid={`feedback-detail-priority-${idx}`}>
              {isDraft && can_manage ? (
                <>
                  <Textarea value={p.observation} onChange={(e) => updatePriority(idx, "observation", e.target.value)} placeholder="Observation" rows={2} data-testid={`feedback-detail-priority-observation-${idx}`} />
                  <Textarea value={p.impact} onChange={(e) => updatePriority(idx, "impact", e.target.value)} placeholder="Impact (optional)" rows={2} />
                  <Textarea value={p.suggestion} onChange={(e) => updatePriority(idx, "suggestion", e.target.value)} placeholder="Suggestion (optional)" rows={2} />
                </>
              ) : (
                <>
                  <p className="text-sm text-ink"><span className="font-medium">Observation:</span> {p.observation}</p>
                  {p.impact && <p className="text-sm text-ink-soft"><span className="font-medium">Impact:</span> {p.impact}</p>}
                  {p.suggestion && <p className="text-sm text-ink-soft"><span className="font-medium">Suggestion:</span> {p.suggestion}</p>}
                </>
              )}
            </div>
          ))}
          {isDraft && can_manage && priorities.length < 3 && (
            <Button variant="ghost" size="sm" onClick={() => setPriorities((p) => [...p, { ...EMPTY_PRIORITY }])} data-testid="feedback-detail-add-priority-btn">
              + Add priority ({priorities.length}/3)
            </Button>
          )}
        </div>

        {isDraft && can_manage && (
          <div className="space-y-4 border-t border-ink/10 pt-6">
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveDraft} disabled={updateDraft.isPending} data-testid="feedback-detail-save-draft-btn"><Pencil size={14} className="mr-1.5" /> Save draft</Button>
              <Button variant="ghost" onClick={doDelete} disabled={deleteDraft.isPending} data-testid="feedback-detail-delete-draft-btn"><Trash2 size={14} className="mr-1.5" /> Discard</Button>
            </div>
            {isTeamWorkspace && <AudienceGate actorRole="coach" teamMembers={uploadContext?.team_members || []} onConfirm={setAudience} />}
            <Button onClick={doPublish} disabled={publish.isPending || (isTeamWorkspace && !audience)} data-testid="feedback-detail-publish-btn">
              <Send size={14} className="mr-1.5" /> Publish
            </Button>
          </div>
        )}

        {!isDraft && (
          <div className="flex flex-wrap gap-2 border-t border-ink/10 pt-6">
            {is_mine && (
              <Button onClick={openResponseRoom} disabled={responseRoom.isPending} data-testid="feedback-detail-respond-btn">
                <DoorOpen size={14} className="mr-1.5" /> Respond in the Rehearsal Room
              </Button>
            )}
            {can_manage && (
              <Button variant="outline" onClick={doAmend} disabled={amend.isPending} data-testid="feedback-detail-amend-btn">
                <Pencil size={14} className="mr-1.5" /> Amend feedback
              </Button>
            )}
          </div>
        )}
      </div>
    </ClientShell>
  );
}
